import React, { useState } from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import config from '../../config';
import { PRINCIPAL_SUBJECTS, SUBJECTS } from '../../config';

const ExportStudentProfiles = () => {
    const [loading, setLoading] = useState(false);

    // Helper function to map provincial prize values to Vietnamese
    const mapProvincialPrizeToVietnamese = (prize) => {
        const prizeMap = {
            '': 'Không có',
            'first': 'Giải nhất',
            'second': 'Giải nhì',
            'third': 'Giải ba'
        };
        return prizeMap[prize] || prize;
    };

    // Helper function to map subject to Vietnamese
    const mapSubjectToVietnamese = (subject) => {
        return PRINCIPAL_SUBJECTS[subject] || subject;
    };

    // Helper function to map grade rank values to Vietnamese
    const mapGradeRankToVietnamese = (rank) => {
        const rankMap = {
            'excellent': 'Tốt',
            'other': 'Khác',
            '': 'Không có'
        };
        return rankMap[rank] || rank;
    };

    const exportToExcel = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(
                `${config.apiUrl}/api/v1/admin/export/student-profiles`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('response', response);

            if (!response.data.success) {
                throw new Error('Failed to fetch data');
            }

            const data = response.data.data;

            // Transform data for Excel
            const excelData = data.map(profile => {
                // Create base object with all fields
                const baseData = {
                    'Mã hồ sơ': profile.student_code,
                    'Ngày tạo tài khoản': profile.registered_at,
                    'Số CCCD': profile.identification,
                    'Họ và tên': profile.full_name,
                    'Ngày sinh': profile.date_of_birth,
                    'Khu vực': profile.admission_area,
                    'Đối tượng': profile.admission_group,
                    'Địa chỉ liên hệ': profile.contact_address,
                    'Tỉnh/Thành phố': profile.province,
                    'Xã/Phường': profile.commune,
                    'Số điện thoại thí sinh': profile.student_phone_number,
                    'Số điện thoại phụ huynh': profile.parents_phone_number,
                    'Điểm IELTS': profile.ielts_grade,
                    'Giải thưởng cấp tỉnh': mapProvincialPrizeToVietnamese(profile.provincial_prize),
                    'Môn giải thưởng': mapSubjectToVietnamese(profile.provincial_prize_subject),
                    'Năm tốt nghiệp THPT': profile.graduation_year,
                    'Xếp loại học lực lớp 10': mapGradeRankToVietnamese(profile.grade_10_rank),
                    'Xếp loại học lực lớp 11': mapGradeRankToVietnamese(profile.grade_11_rank),
                    'Xếp loại học lực lớp 12': mapGradeRankToVietnamese(profile.grade_12_rank),
                    'Đợt xét tuyển': profile.period,
                    'Email': profile.email,
                    'Số điện thoại': profile.phone,
                    'Số nguyện vọng': profile.number_of_admissions,
                    'Nộp lệ phí': profile.is_paid ? 'Đã nộp' : 'Chưa nộp'
                };

                // Initialize all admission columns with empty values
                for (let i = 1; i <= 5; i++) {
                    baseData[`Nguyện vọng ${i}`] = '';
                }

                // Set first admission from StudentProfile
                baseData['Nguyện vọng 1'] = profile.first_admission || '';

                // Add other admissions from admissions table
                const sortedAdmissions = profile.admissions
                    .filter(admission => admission.priority > 1 && admission.priority <= 5)
                    .sort((a, b) => a.priority - b.priority);

                sortedAdmissions.forEach(admission => {
                    baseData[`Nguyện vọng ${admission.priority}`] = admission.subject || '';
                });

                // Add grade 10 scores
                Object.entries(profile.grades || {}).forEach(([key, value]) => {
                    if (key.endsWith('_10')) {
                        const subject = key.replace('_10', '');
                        baseData[`Điểm ${SUBJECTS[subject]} 10`] = value;
                    }
                });

                // Add grade 11 scores
                Object.entries(profile.grades || {}).forEach(([key, value]) => {
                    if (key.endsWith('_11')) {
                        const subject = key.replace('_11', '');
                        baseData[`Điểm ${SUBJECTS[subject]} 11`] = value;
                    }
                });

                // Add grade 12 scores
                Object.entries(profile.grades || {}).forEach(([key, value]) => {
                    if (key.endsWith('_12')) {
                        const subject = key.replace('_12', '');
                        baseData[`Điểm ${SUBJECTS[subject]} 12`] = value;
                    }
                });

                return baseData;
            });

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 15 }, // Mã số hồ sơ
                { wch: 18 }, // Ngày tạo tài khoản
                { wch: 15 }, // Số CCCD/CMND
                { wch: 25 }, // Họ và tên
                { wch: 12 }, // Ngày sinh
                { wch: 15 }, // Khu vực tuyển sinh
                { wch: 15 }, // Đối tượng tuyển sinh
                { wch: 30 }, // Địa chỉ liên hệ
                { wch: 25 }, // Tỉnh/Thành phố
                { wch: 25 }, // Xã/Phường
                { wch: 20 }, // Số điện thoại sinh viên
                { wch: 20 }, // Số điện thoại phụ huynh
                { wch: 12 }, // Điểm IELTS
                { wch: 20 }, // Giải thưởng cấp tỉnh
                { wch: 20 }, // Môn giải thưởng
                { wch: 15 }, // Năm tốt nghiệp THPT
                { wch: 15 }, // Xếp loại học lực lớp 10
                { wch: 15 }, // Xếp loại học lực lớp 11
                { wch: 15 }, // Xếp loại học lực lớp 12
                { wch: 20 }, // Đợt xét tuyển
                { wch: 25 }, // Email
                { wch: 15 }, // Số điện thoại
                { wch: 15 }, // Số nguyện vọng
                { wch: 15 }, // Nộp lệ phí
                { wch: 30 }, // Nguyện vọng 1
                { wch: 30 }, // Nguyện vọng 2
                { wch: 30 }, // Nguyện vọng 3
                { wch: 30 }, // Nguyện vọng 4
                { wch: 30 }, // Nguyện vọng 5
                // Điểm lớp 10
                { wch: 12 }, // Điểm Toán 10
                { wch: 12 }, // Điểm Văn 10
                { wch: 12 }, // Điểm Anh 10
                { wch: 12 }, // Điểm Lý 10
                { wch: 12 }, // Điểm Hóa 10
                { wch: 12 }, // Điểm Sử 10
                { wch: 12 }, // Điểm Địa 10
                { wch: 12 }, // Điểm Giáo dục kinh tế và pháp luật 10
                { wch: 12 }, // Điểm Công nghệ 10
                { wch: 12 }, // Điểm Tin học 10
                // Điểm lớp 11
                { wch: 12 }, // Điểm Toán 11
                { wch: 12 }, // Điểm Văn 11
                { wch: 12 }, // Điểm Anh 11
                { wch: 12 }, // Điểm Lý 11
                { wch: 12 }, // Điểm Hóa 11
                { wch: 12 }, // Điểm Sử 11
                { wch: 12 }, // Điểm Địa 11
                { wch: 12 }, // Điểm Giáo dục kinh tế và pháp luật 11
                { wch: 12 }, // Điểm Công nghệ 11
                { wch: 12 }, // Điểm Tin học 11
                // Điểm lớp 12
                { wch: 12 }, // Điểm Toán 12
                { wch: 12 }, // Điểm Văn 12
                { wch: 12 }, // Điểm Anh 12
                { wch: 12 }, // Điểm Lý 12
                { wch: 12 }, // Điểm Hóa 12
                { wch: 12 }, // Điểm Sử 12
                { wch: 12 }, // Điểm Địa 12
                { wch: 12 }, // Điểm Giáo dục kinh tế và pháp luật 12
                { wch: 12 }, // Điểm Công nghệ 12
                { wch: 12 }, // Điểm Tin học 12
                { wch: 50 }  // Tài liệu đính kèm
            ];
            ws['!cols'] = colWidths;

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'DS chưa đầy đủ');

            // Generate filename with current datetime
            const now = new Date();
            const formattedDate = now.toLocaleDateString('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).replace(/\//g, '-');
            const formattedTime = now.toLocaleTimeString('vi-VN', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }).replace(/:/g, '-');
            const filename = `danh-sach-dang-ky-${formattedDate}_${formattedTime}.xlsx`;

            // Generate Excel file
            XLSX.writeFile(wb, filename);

            message.success('Xuất file Excel thành công!');

        } catch (error) {
            console.error('Error exporting data:', error);
            message.error('Có lỗi xảy ra khi xuất file Excel: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportToExcel}
            loading={loading}
        >
            Xuất Excel
        </Button>
    );
};

export default ExportStudentProfiles; 